import { isDiskVideo, toDiskFolderVideo } from 'modules/match-video/domain/data-mappers';
import { YandexDiskLinkDto, YandexDiskPublicResourceDto } from 'modules/match-video/domain/dtos';
import { DiskFolderVideo } from 'modules/match-video/domain/interfaces';

const API = 'https://cloud-api.yandex.net/v1/disk/public/resources';
/** Folder page size. The API defaults to 20; one page covers a match folder (9 clips in the sample). */
export const DISK_PAGE_SIZE = 100;
const HTTP_NOT_FOUND = 404;

/**
 * - `not-found` — no such public resource, or the owner closed it;
 * - `not-folder` — the link points to a file;
 * - `network` — the browser got no response: offline, or a host refused CORS;
 * - `http` — any other refusal.
 */
export type YandexDiskErrorCode = 'not-found' | 'not-folder' | 'network' | 'http';

export class YandexDiskError extends Error {
  constructor(
    readonly code: YandexDiskErrorCode,
    readonly status?: number,
  ) {
    super(status ? `${code} (${status})` : code);
    this.name = 'YandexDiskError';
  }
}

export interface DiskDownloadOptions {
  signal?: AbortSignal;
  /** Bytes received and the total from `Content-Length` (0 when the server did not send it). */
  onProgress?: (loaded: number, total: number) => void;
}

const isAbort = (error: unknown) => (error as Error | undefined)?.name === 'AbortError';

async function request(url: string, signal?: AbortSignal): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch (error) {
    if (isAbort(error)) throw error;
    // `fetch` rejects with a TypeError both offline and when CORS blocks the response.
    throw new YandexDiskError('network');
  }
  if (!response.ok) {
    throw new YandexDiskError(response.status === HTTP_NOT_FOUND ? 'not-found' : 'http', response.status);
  }
  return response;
}

async function readWithProgress(response: Response, options: DiskDownloadOptions): Promise<Blob> {
  const total = Number(response.headers.get('Content-Length')) || 0;
  const type = response.headers.get('Content-Type') ?? '';
  if (!response.body) {
    const blob = await response.blob();
    options.onProgress?.(blob.size, total || blob.size);
    return blob;
  }
  const reader = response.body.getReader();
  const chunks: BlobPart[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    options.onProgress?.(loaded, total);
  }
  return new Blob(chunks, { type });
}

/**
 * Public Yandex Disk folders over the REST API (https://yandex.ru/dev/disk-api/doc/ru/reference/public). No token:
 * a public resource is read anonymously, and `cloud-api.yandex.net` answers CORS for any origin. Albums (`/a/`) have
 * no API at all — `parseDiskLink` stops them before a request.
 */
export class YandexDiskPublicRepository {
  /** Videos at the top level of the folder, every page; sub-folders are not walked. */
  async listFolderVideos(publicKey: string, signal?: AbortSignal): Promise<DiskFolderVideo[]> {
    const videos: DiskFolderVideo[] = [];
    let offset = 0;
    for (;;) {
      const query = new URLSearchParams({
        public_key: publicKey,
        limit: String(DISK_PAGE_SIZE),
        offset: String(offset),
        sort: 'name',
      });
      const response = await request(`${API}?${query}`, signal);
      const resource: YandexDiskPublicResourceDto = await response.json();
      if (resource.type !== 'dir') {
        throw new YandexDiskError('not-folder');
      }
      const page = resource._embedded;
      if (!page) break;
      videos.push(...page.items.filter(isDiskVideo).map(toDiskFolderVideo));
      offset += page.items.length;
      if (page.items.length === 0 || offset >= page.total) break;
    }
    return videos;
  }

  /** Downloads one file of the folder into memory: the API gives a one-off link, the file comes from it. */
  async downloadFile(publicKey: string, path: string, options: DiskDownloadOptions = {}): Promise<Blob> {
    const query = new URLSearchParams({ public_key: publicKey, path });
    const linkResponse = await request(`${API}/download?${query}`, options.signal);
    const link: YandexDiskLinkDto = await linkResponse.json();
    const fileResponse = await request(link.href, options.signal);
    return readWithProgress(fileResponse, options);
  }
}

export const yandexDiskPublicRepository = new YandexDiskPublicRepository();
